/**
 * The Channel Deep-dive's naming rules (spec §4, rulings R12, R13 and R16).
 * Central rules in code, no workspace override:
 * - campaign type: segment 2 of the campaign name, normalised exactly as
 *   campaignNaming.ts does, matched to one BUY_TYPES entry (null = no type; "Unclassified" is the
 *   whole-name rule, classifyCampaignName, rules-04 E1);
 * - targeting family: the ad set's first segment after its leading "[…]" tags,
 *   matched by prefix to CORE / LAL / BROAD / RTG, else UNCLASSIFIED;
 * - the name of an unnamed campaign or ad set: its id's first 8 characters, one rule for the filter dropdown and the
 *   tables; an unnamed ad reads as on D3, "(ไม่มีชื่อ) · <external id>" (owner, 2026-10-16).
 * Pure.
 *
 * @module services/channels/channelNaming
 */
import { BUY_TYPES, norm } from '@/services/looker/campaignNaming';
import type { TargetingFamily } from './channelTypes';

/** Every BUY_TYPES entry with its normalised key, in BUY_TYPES order. */
const BUY_KEYS: ReadonlyArray<readonly [key: string, type: string]> = BUY_TYPES.map((t) => [norm(t), t] as const);

/**
 * The BUY_TYPES entry that segment 2 names (R12); null = no type. Like
 * classifyCampaignName it needs two segments. Synonyms stay separate entries
 * ('Conversion' ≠ 'Conversions'). 'YT In-Stream' and 'YT In Stream' share one
 * key, 'ytinstream': the first in BUY_TYPES order wins, so both read 'YT In-Stream'.
 */
export const campaignTypeOf = (name: string | null): string | null => {
  const segs = (name ?? '').split('|');
  if (segs.length < 2) return null;
  const key = norm(segs[1]);
  return BUY_KEYS.find(([k]) => k === key)?.[1] ?? null;
};

/** Every leading "[…]" tag with the whitespace around it, however many (R13). */
export const LEADING_TAGS = /^(\s*\[[^\]]*\]\s*)+/;

/**
 * R13's prefixes. None of the eight starts with another, so the order only
 * groups them for reading: at most one can match.
 */
const FAMILY_PREFIXES: ReadonlyArray<readonly [prefix: string, family: TargetingFamily]> = [
  ['CORE', 'CORE'],
  ['LAL', 'LAL'], ['LOOKALIKE', 'LAL'],
  ['BROAD', 'BROAD'],
  ['RTG', 'RTG'], ['RMKT', 'RTG'], ['RETARGET', 'RTG'], ['REMARKETING', 'RTG'],
];

/**
 * The ad set's targeting family (R13): strip every leading tag, take the first
 * "|" segment, collapse whitespace, upper-case, prefix-match. A family word in
 * a later segment does not count. null and '' → UNCLASSIFIED.
 */
export const targetingFamilyOf = (adSetName: string | null): TargetingFamily => {
  const first = (adSetName ?? '').replace(LEADING_TAGS, '').split('|')[0];
  const head = first.replace(/\s+/g, ' ').trim().toUpperCase();
  return FAMILY_PREFIXES.find(([prefix]) => head.startsWith(prefix))?.[1] ?? 'UNCLASSIFIED';
};

/** An unnamed entity shows its id's first 8 characters (D1's box 12 convention). */
export const idPrefix = (id: string): string => id.slice(0, 8);
